import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { ArrowRight, BookOpenCheck, Check, ChevronDown, FileSpreadsheet, LockKeyhole, Search, ShieldCheck, UserRound, UsersRound } from 'lucide-react';

import { api, type AcademicYear, type Page } from './api';
import { registerToast, setMessageToast } from './toast';
import { TeachersPanel } from './TeachersPanel';

type Guardian = {
  fullName: string;
  yearOfBirth: string;
  education: string;
  occupation: string;
  income: string;
  nik: string;
  phone: string;
  email: string;
};
type FormState = {
  fullName: string;
  nisn: string;
  nik: string;
  nipd: string;
  familyCardNumber: string;
  gender: string;
  birthPlace: string;
  birthDate: string;
  religion: string;
  residenceType: string;
  transportation: string;
  phoneHome: string;
  mobile: string;
  email: string;
  previousSchool: string;
  specialNeeds: string;
  achievements: string;
  address: {
    street: string; rt: string; rw: string; hamlet: string; village: string; district: string;
    regency: string; province: string; postalCode: string; lat: string; lng: string;
  };
  periodic: {
    weightKg: string; heightCm: string; headCircumferenceCm: string; siblingCount: string;
    childOrder: string; distanceKm: string;
  };
  program: {
    kps: string; kpsNumber: string; kip: string; kipNumber: string; kipReceived: string;
    kksNumber: string; pipEligible: string; pipReason: string; bos: string; pipStatus: string;
    pipKip: string; pipPkh: string; pipSktm: string; pipKks: string; pipOrphanLetter: string;
    bank: string; accountNumber: string; accountHolder: string;
  };
  examNumber: string;
  skhunNumber: string;
  guardians: { father: Guardian; mother: Guardian; guardian: Guardian };
  enrollment: { level: string; grade: string; className: string; academicYear: string; term: string };
  consent: boolean;
};

const emptyGuardian = (): Guardian => ({
  fullName: '', yearOfBirth: '', education: '', occupation: '', income: '', nik: '', phone: '', email: '',
});
const initialForm = (): FormState => ({
  fullName: '', nisn: '', nik: '', nipd: '', familyCardNumber: '', gender: '', birthPlace: '',
  birthDate: '', religion: '', residenceType: '', transportation: '', phoneHome: '', mobile: '',
  email: '', previousSchool: '', specialNeeds: '', achievements: '',
  address: {
    street: '', rt: '', rw: '', hamlet: '', village: '', district: '', regency: '', province: '',
    postalCode: '', lat: '', lng: '',
  },
  periodic: {
    weightKg: '', heightCm: '', headCircumferenceCm: '', siblingCount: '', childOrder: '', distanceKm: '',
  },
  program: {
    kps: '', kpsNumber: '', kip: '', kipNumber: '', kipReceived: '', kksNumber: '', pipEligible: '',
    pipReason: '', bos: '', pipStatus: '', pipKip: '', pipPkh: '', pipSktm: '', pipKks: '',
    pipOrphanLetter: '', bank: '', accountNumber: '', accountHolder: '',
  },
  examNumber: '', skhunNumber: '',
  guardians: { father: emptyGuardian(), mother: emptyGuardian(), guardian: emptyGuardian() },
  enrollment: { level: 'SD', grade: '', className: '', academicYear: '', term: '' },
  consent: false,
});

type FieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
};

function Field({ label, value, onChange, type = 'text', required, placeholder, hint }: FieldProps) {
  return (
    <label className="field">
      <span>{label}{required && <i> *</i>}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        placeholder={placeholder}
        maxLength={type === 'text' ? 200 : undefined}
      />
      {hint && <small>{hint}</small>}
    </label>
  );
}

function SelectField({ label, value, onChange, choices, required }: FieldProps & { choices: string[] }) {
  return (
    <label className="field">
      <span>{label}{required && <i> *</i>}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} required={required}>
        <option value="">Pilih…</option>
        {choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}
      </select>
    </label>
  );
}

function Section({ title, subtitle, icon, children, open = false }: {
  title: string; subtitle: string; icon: ReactNode; children: ReactNode; open?: boolean;
}) {
  return (
    <details className="form-section" open={open}>
      <summary>
        <span className="section-icon">{icon}</span>
        <span className="section-heading"><strong>{title}</strong><small>{subtitle}</small></span>
        <ChevronDown size={18} />
      </summary>
      <div className="section-content">{children}</div>
    </details>
  );
}

function PublicForm() {
  const [form, setForm] = useState(initialForm);
  const [years, setYears] = useState<AcademicYear[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'sent' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const setTop = (key: keyof FormState, value: string | boolean) => setForm((current) => ({ ...current, [key]: value }));
  const setAddress = (key: keyof FormState['address'], value: string) => setForm((current) => ({ ...current, address: { ...current.address, [key]: value } }));
  const setPeriodic = (key: keyof FormState['periodic'], value: string) => setForm((current) => ({ ...current, periodic: { ...current.periodic, [key]: value } }));
  const setProgram = (key: keyof FormState['program'], value: string) => setForm((current) => ({ ...current, program: { ...current.program, [key]: value } }));
  const setGuardian = (person: keyof FormState['guardians'], key: keyof Guardian, value: string) =>
    setForm((current) => ({ ...current, guardians: { ...current.guardians, [person]: { ...current.guardians[person], [key]: value } } }));
  const setEnrollment = (key: keyof FormState['enrollment'], value: string) =>
    setForm((current) => ({ ...current, enrollment: { ...current.enrollment, [key]: value } }));

  useEffect(() => {
    api<{ items: AcademicYear[] }>('/public/academic-years')
      .then(({ items }) => { setYears(items); setState('ready'); })
      .catch(() => { setState('error'); setMessage('Form belum dapat dibuka. Silakan hubungi sekolah.'); });
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    const asBoolean = (value: string) => value === '' ? null : value === 'true';
    const payload = {
      ...form,
      gender: form.gender === 'Laki-laki' ? 'MALE' : form.gender === 'Perempuan' ? 'FEMALE' : null,
      nisn: form.nisn || null,
      nik: form.nik || null,
      nipd: form.nipd || null,
      familyCardNumber: form.familyCardNumber || null,
      birthDate: form.birthDate || null,
      address: {
        ...form.address,
        lat: form.address.lat || null,
        lng: form.address.lng || null,
      },
      periodic: { ...form.periodic },
      program: {
        ...form.program,
        kps: asBoolean(form.program.kps),
        kip: asBoolean(form.program.kip),
        kipReceived: asBoolean(form.program.kipReceived),
        pipEligible: asBoolean(form.program.pipEligible),
        bos: asBoolean(form.program.bos),
      },
      guardians: {
        father: { ...form.guardians.father, yearOfBirth: form.guardians.father.yearOfBirth || null, nik: form.guardians.father.nik || null },
        mother: { ...form.guardians.mother, yearOfBirth: form.guardians.mother.yearOfBirth || null, nik: form.guardians.mother.nik || null },
        guardian: { ...form.guardians.guardian, yearOfBirth: form.guardians.guardian.yearOfBirth || null, nik: form.guardians.guardian.nik || null },
      },
      enrollment: {
        ...form.enrollment,
        grade: form.enrollment.grade || null,
        term: form.enrollment.term || null,
      },
      specialNeeds: form.specialNeeds.split(',').map((item) => item.trim()).filter(Boolean),
      achievements: form.achievements.split(',').map((item) => item.trim()).filter(Boolean),
      website,
    };
    try {
      await api('/public/submissions', { method: 'POST', body: JSON.stringify(payload) });
      setState('sent');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Data belum berhasil dikirim.');
    }
  };

  if (state === 'loading') return <main className="loading">Memuat formulir…</main>;
  if (state === 'sent') {
    return (
      <main className="success-card">
        <span className="success-icon"><Check size={30} /></span>
        <p className="eyebrow">FORMULIR TERKIRIM</p>
        <h1>Terima kasih, data sudah kami terima.</h1>
        <p>Data masih menunggu pencocokan dan verifikasi oleh sekolah. Pengiriman formulir belum berarti pendaftaran telah disetujui.</p>
        <button className="button secondary" onClick={() => { setForm(initialForm()); setState('ready'); }}>Kirim data lainnya</button>
      </main>
    );
  }
  if (state === 'error') return <main className="loading">{message}</main>;

  return (
    <main className="public-shell">
      <header className="public-header">
        <a className="brand" href="/"><span className="brand-mark"><BookOpenCheck size={22} /></span><span>DATA PESERTA DIDIK<small>PORTAL PENDAFTARAN SEKOLAH</small></span></a>
        <a className="staff-link" href="/staff"><LockKeyhole size={15} /> Staf sekolah</a>
      </header>
      <div className="form-hero">
        <div className="hero-copy">
          <p className="eyebrow">PENDAFTARAN PESERTA DIDIK</p>
          <h1>Mulai dengan data yang tepat.</h1>
          <p>Lengkapi profil peserta didik dan keluarga. Tim sekolah akan memeriksa data ini dengan dokumen Dapodik sebelum mengonfirmasi pendaftaran.</p>
          <div className="privacy-note"><ShieldCheck size={19} /><span><strong>Data pribadi terlindungi</strong><small>Akses hanya untuk petugas sekolah yang berwenang.</small></span></div>
        </div>
        <div className="hero-art" aria-hidden="true"><div className="art-circle"><UsersRound size={74} strokeWidth={1.2} /></div><div className="art-card card-one"><span className="art-dot" /> Data keluarga</div><div className="art-card card-two"><Check size={15} /> Terverifikasi</div><div className="art-sparkle">✳</div></div>
      </div>

      <form className="form-card" onSubmit={submit}>
        <div className="form-card-header">
          <div><p className="eyebrow">FORMULIR DIGITAL</p><h2>Data calon peserta didik</h2><p>Kolom bertanda <i>*</i> wajib diisi. Anda dapat melengkapi data lain jika tersedia.</p></div>
          <span className="step-pill">01 <span>/</span> 04</span>
        </div>
        {message && <div role="alert" className="alert">{message}</div>}

        <Section title="Identitas peserta didik" subtitle="Identitas dan data pendidikan" icon={<UserRound size={18} />} open>
          <div className="field-grid">
            <Field label="Nama lengkap" value={form.fullName} onChange={(v) => setTop('fullName', v)} required />
            <Field label="NISN" value={form.nisn} onChange={(v) => setTop('nisn', v)} hint="10 digit; boleh dikosongkan jika belum ada" />
            <Field label="NIK peserta didik" value={form.nik} onChange={(v) => setTop('nik', v)} hint="16 digit; salah satu NISN atau NIK diperlukan" />
            <Field label="NIPD" value={form.nipd} onChange={(v) => setTop('nipd', v)} />
            <Field label="Nomor Kartu Keluarga" value={form.familyCardNumber} onChange={(v) => setTop('familyCardNumber', v)} />
            <SelectField label="Jenis kelamin" value={form.gender} onChange={(v) => setTop('gender', v)} choices={['Laki-laki', 'Perempuan']} />
            <Field label="Tempat lahir" value={form.birthPlace} onChange={(v) => setTop('birthPlace', v)} />
            <Field label="Tanggal lahir" value={form.birthDate} onChange={(v) => setTop('birthDate', v)} type="date" />
            <Field label="Agama" value={form.religion} onChange={(v) => setTop('religion', v)} />
            <Field label="Sekolah asal" value={form.previousSchool} onChange={(v) => setTop('previousSchool', v)} />
            <Field label="Nomor telepon rumah" value={form.phoneHome} onChange={(v) => setTop('phoneHome', v)} />
            <Field label="Nomor HP" value={form.mobile} onChange={(v) => setTop('mobile', v)} />
            <Field label="Email" value={form.email} onChange={(v) => setTop('email', v)} type="email" />
            <Field label="Jenis tempat tinggal" value={form.residenceType} onChange={(v) => setTop('residenceType', v)} />
            <Field label="Alat transportasi" value={form.transportation} onChange={(v) => setTop('transportation', v)} />
            <Field label="Kebutuhan khusus" value={form.specialNeeds} onChange={(v) => setTop('specialNeeds', v)} hint="Pisahkan beberapa keterangan dengan koma; isi bila relevan." />
            <Field label="Prestasi / penghargaan" value={form.achievements} onChange={(v) => setTop('achievements', v)} hint="Pisahkan beberapa prestasi dengan koma, bila ada." />
            <Field label="Nomor peserta ujian nasional" value={form.examNumber} onChange={(v) => setTop('examNumber', v)} />
            <Field label="Nomor seri ijazah / SKHUN" value={form.skhunNumber} onChange={(v) => setTop('skhunNumber', v)} />
          </div>
          <div className="subsection-title">Tahun ajaran pendaftaran</div>
          <div className="field-grid">
            <SelectField label="Jenjang" value={form.enrollment.level} onChange={(v) => setEnrollment('level', v)} choices={['KB', 'SD', 'SMP', 'SMA']} required />
            <label className="field"><span>Tahun ajaran <i>*</i></span><select value={form.enrollment.academicYear} onChange={(e) => setEnrollment('academicYear', e.target.value)} required><option value="">Pilih…</option>{years.map((year) => <option key={year.id} value={year.label}>{year.label}</option>)}</select></label>
            <Field label="Kelas / rombel" value={form.enrollment.className} onChange={(v) => setEnrollment('className', v)} />
            <Field label="Tingkat kelas" value={form.enrollment.grade} onChange={(v) => setEnrollment('grade', v)} type="number" />
            <label className="field"><span>Semester</span><select value={form.enrollment.term} onChange={(e) => setEnrollment('term', e.target.value)}><option value="">Pilih…</option><option value="GANJIL">Ganjil</option><option value="GENAP">Genap</option></select></label>
          </div>
        </Section>

        <Section title="Alamat tempat tinggal" subtitle="Alamat dan lokasi tempat tinggal" icon={<BookOpenCheck size={18} />}>
          <div className="field-grid">
            <Field label="Alamat lengkap" value={form.address.street} onChange={(v) => setAddress('street', v)} />
            <Field label="RT" value={form.address.rt} onChange={(v) => setAddress('rt', v)} />
            <Field label="RW" value={form.address.rw} onChange={(v) => setAddress('rw', v)} />
            <Field label="Dusun" value={form.address.hamlet} onChange={(v) => setAddress('hamlet', v)} />
            <Field label="Desa / kelurahan" value={form.address.village} onChange={(v) => setAddress('village', v)} />
            <Field label="Kecamatan" value={form.address.district} onChange={(v) => setAddress('district', v)} />
            <Field label="Kabupaten / kota" value={form.address.regency} onChange={(v) => setAddress('regency', v)} />
            <Field label="Provinsi" value={form.address.province} onChange={(v) => setAddress('province', v)} />
            <Field label="Kode pos" value={form.address.postalCode} onChange={(v) => setAddress('postalCode', v)} />
            <Field label="Lintang (opsional)" value={form.address.lat} onChange={(v) => setAddress('lat', v)} type="number" />
            <Field label="Bujur (opsional)" value={form.address.lng} onChange={(v) => setAddress('lng', v)} type="number" />
          </div>
        </Section>

        <Section title="Data orang tua dan wali" subtitle="Isi data orang tua atau wali yang tersedia" icon={<UsersRound size={18} />}>
          {(['father', 'mother', 'guardian'] as const).map((person) => {
            const title = person === 'father' ? 'Ayah' : person === 'mother' ? 'Ibu' : 'Wali';
            const values = form.guardians[person];
            return <div className="guardian-block" key={person}>
              <div className="subsection-title">{title}</div>
              <div className="field-grid">
                <Field label={`Nama ${title.toLowerCase()}`} value={values.fullName} onChange={(v) => setGuardian(person, 'fullName', v)} />
                <Field label="NIK" value={values.nik} onChange={(v) => setGuardian(person, 'nik', v)} />
                <Field label="Tahun lahir" value={values.yearOfBirth} onChange={(v) => setGuardian(person, 'yearOfBirth', v)} type="number" />
                <Field label="Pendidikan" value={values.education} onChange={(v) => setGuardian(person, 'education', v)} />
                <Field label="Pekerjaan" value={values.occupation} onChange={(v) => setGuardian(person, 'occupation', v)} />
                <Field label="Penghasilan" value={values.income} onChange={(v) => setGuardian(person, 'income', v)} />
                <Field label="Nomor HP" value={values.phone} onChange={(v) => setGuardian(person, 'phone', v)} />
                <Field label="Email" value={values.email} onChange={(v) => setGuardian(person, 'email', v)} type="email" />
              </div>
            </div>;
          })}
        </Section>

        <Section title="Data periodik dan bantuan" subtitle="Data kesehatan, keluarga, serta program bantuan" icon={<FileSpreadsheet size={18} />}>
          <div className="subsection-title">Data periodik</div>
          <div className="field-grid">
            <Field label="Berat badan (kg)" value={form.periodic.weightKg} onChange={(v) => setPeriodic('weightKg', v)} type="number" />
            <Field label="Tinggi badan (cm)" value={form.periodic.heightCm} onChange={(v) => setPeriodic('heightCm', v)} type="number" />
            <Field label="Lingkar kepala (cm)" value={form.periodic.headCircumferenceCm} onChange={(v) => setPeriodic('headCircumferenceCm', v)} type="number" />
            <Field label="Jumlah saudara kandung" value={form.periodic.siblingCount} onChange={(v) => setPeriodic('siblingCount', v)} type="number" />
            <Field label="Anak ke-" value={form.periodic.childOrder} onChange={(v) => setPeriodic('childOrder', v)} type="number" />
            <Field label="Jarak rumah ke sekolah (km)" value={form.periodic.distanceKm} onChange={(v) => setPeriodic('distanceKm', v)} type="number" />
          </div>
          <div className="subsection-title">Program bantuan dan rekening</div>
          <div className="field-grid">
            {([
              ['Penerima KPS', 'kps'], ['Penerima KIP', 'kip'], ['Nama tercatat di KIP', 'kipReceived'],
              ['Layak PIP', 'pipEligible'], ['Penerima BOS', 'bos'],
            ] as const).map(([label, key]) => (
              <label className="field" key={key}><span>{label}</span><select value={form.program[key]} onChange={(e) => setProgram(key, e.target.value)}><option value="">Pilih…</option><option value="true">Ya</option><option value="false">Tidak</option></select></label>
            ))}
            {([
              ['Nomor KPS', 'kpsNumber'], ['Nomor KIP', 'kipNumber'], ['Nomor KKS', 'kksNumber'],
              ['Alasan layak PIP', 'pipReason'], ['Status PIP', 'pipStatus'], ['PIP - KIP', 'pipKip'],
              ['PIP - PKH', 'pipPkh'], ['PIP - SKTM', 'pipSktm'], ['PIP - KKS', 'pipKks'],
              ['Surat panti asuhan', 'pipOrphanLetter'], ['Nama bank', 'bank'], ['Nomor rekening', 'accountNumber'],
              ['Nama pemilik rekening', 'accountHolder'],
            ] as const).map(([label, key]) => <Field key={key} label={label} value={form.program[key]} onChange={(v) => setProgram(key, v)} />)}
          </div>
        </Section>

        <input className="honeypot" name="website" value={website} onChange={(event) => setWebsite(event.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <div className="consent-row">
          <input id="consent" type="checkbox" checked={form.consent} onChange={(e) => setTop('consent', e.target.checked)} required />
          <label htmlFor="consent">Saya menyatakan data yang diberikan benar dan menyetujui penggunaannya untuk verifikasi serta administrasi pendidikan oleh sekolah.</label>
        </div>
        <div className="form-footer"><span><ShieldCheck size={16} /> Data dikirim melalui koneksi aman</span><button className="button primary" type="submit">Kirim untuk ditinjau <ArrowRight size={17} /></button></div>
      </form>
      <footer className="public-footer">Data peserta didik · Informasi hanya digunakan untuk administrasi sekolah</footer>
    </main>
  );
}

type StudentListItem = { id: string; fullName: string; nisn: string | null; nik: string | null; nipd: string | null; enrollments: Array<{ level: string; grade: number | null; className: string | null; classKey?: string | null; academicYear: { label: string } }> };
type ClassOption = { academicYearId: string; academicYearLabel: string; level: string; classKey: string; className: string | null; studentCount: number };
type StudentFilters = { level: string; classKey: string; academicYearId: string };
const emptyStudentFilters: StudentFilters = { level: '', classKey: '', academicYearId: '' };

function studentQuery(search: string, filters: StudentFilters): string {
  const params = new URLSearchParams();
  if (search) params.set('q', search);
  if (filters.level) params.set('level', filters.level);
  if (filters.classKey) params.set('classKey', filters.classKey);
  if (filters.academicYearId) params.set('academicYearId', filters.academicYearId);
  return params.toString();
}
type Submission = { id: string; status: string; profile: { fullName?: string; nisn?: string | null; nik?: string | null; enrollment?: { level?: string; academicYear?: string } }; conflictCode: string | null; submittedAt: string; matchedStudent: { fullName: string; nisn: string | null; nik: string | null } | null };
type Batch = { id: string; status: string; rowCount: number; quarantinedCount: number; adapter: string; fileName: string };
type ImportResult = { batch: Batch; preview: Array<{ sourceRowNo: number; outcome: string; reasonCodes: string[] }>; warningCodes: string[]; duplicate?: boolean };

function StaffApp() {
  const [user, setUser] = useState<{ fullName: string; email: string } | null>(null);
  const [tab, setTab] = useState<'students' | 'teachers' | 'submissions' | 'import'>('students');
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [studentTotal, setStudentTotal] = useState(0);
  const [studentPage, setStudentPage] = useState(1);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [years, setYears] = useState<AcademicYear[]>([]);
  const [search, setSearch] = useState('');
  const [studentFilters, setStudentFilters] = useState<StudentFilters>(emptyStudentFilters);
  const [classOptions, setClassOptions] = useState<ClassOption[]>([]);
  const [status, setStatus] = useState('PENDING');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState<ImportResult | null>(null);
  const [academicYearId, setAcademicYearId] = useState('');
  const [level, setLevel] = useState('SD');
  const [file, setFile] = useState<File | null>(null);
  const [candidateQuery, setCandidateQuery] = useState('');
  const [candidateLookup, setCandidateLookup] = useState<{ submissionId: string; items: StudentListItem[] } | null>(null);
  const [importCandidates, setImportCandidates] = useState<{ sourceRowNo: number; items: StudentListItem[] } | null>(null);

  const loadData = async () => {
    try {
      const { user: active } = await api<{ user: { fullName: string; email: string } }>('/auth/me');
      setUser(active);
      const [studentResult, submissionResult, yearResult] = await Promise.all([
        api<Page<StudentListItem>>(`/staff/students?${studentQuery(search, studentFilters)}`),
        api<Page<Submission>>(`/staff/submissions?status=${status}`),
        api<{ items: AcademicYear[] }>('/staff/academic-years'),
      ]);
      setStudents(studentResult.items);
      setStudentTotal(studentResult.pagination.total);
      setStudentPage(1);
      setSubmissions(submissionResult.items);
      setYears(yearResult.items);
      if (!academicYearId && yearResult.items[0]) setAcademicYearId(yearResult.items[0].id);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => { void loadData(); }, [status]);
  useEffect(() => {
    if (user) {
      const timer = window.setTimeout(() => {
        api<Page<StudentListItem>>(`/staff/students?${studentQuery(search, studentFilters)}`)
          .then((result) => {
            setStudents(result.items);
            setStudentTotal(result.pagination.total);
            setStudentPage(1);
          })
          .catch(() => setError('Daftar peserta didik gagal dimuat.'));
      }, 250);
      return () => window.clearTimeout(timer);
    }
  }, [search, studentFilters, user]);
  const loadMoreStudents = async () => {
    const next = studentPage + 1;
    try {
      const params = new URLSearchParams(studentQuery(search, studentFilters));
      params.set('page', String(next));
      const result = await api<Page<StudentListItem>>(`/staff/students?${params}`);
      setStudents((current) => [...current, ...result.items]);
      setStudentTotal(result.pagination.total);
      setStudentPage(next);
    } catch {
      setError('Data berikutnya gagal dimuat.');
    }
  };
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams();
    if (studentFilters.level) params.set('level', studentFilters.level);
    if (studentFilters.academicYearId) params.set('academicYearId', studentFilters.academicYearId);
    api<{ items: ClassOption[] }>(`/staff/classes?${params}`)
      .then((result) => {
        const unique = new Map<string, ClassOption>();
        for (const option of result.items) {
          const key = `${option.level}|${option.classKey}`;
          const current = unique.get(key);
          if (!current) unique.set(key, option);
          else unique.set(key, { ...current, studentCount: current.studentCount + option.studentCount });
        }
        setClassOptions([...unique.values()]);
        setStudentFilters((current) => {
          if (!current.classKey) return current;
          const available = [...unique.values()].some((option) => option.classKey === current.classKey);
          return available ? current : { ...current, classKey: '' };
        });
      })
      .catch(() => setError('Daftar kelas gagal dimuat.'));
  }, [studentFilters.level, studentFilters.academicYearId, user]);

  const signOut = async () => {
    await api('/auth/logout', { method: 'POST' });
    setUser(null);
  };
  const review = async (id: string, action: 'confirm' | 'reject') => {
    setBusy(true);
    setError('');
    try {
      await api(`/staff/submissions/${id}/${action}`, { method: 'POST' });
      await loadData();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Tindakan tidak berhasil.');
    } finally { setBusy(false); }
  };
  const findCandidates = async (submissionId: string) => {
    if (!candidateQuery.trim()) {
      setError('Masukkan nama, NISN, atau NIK untuk mencari data resmi.');
      return;
    }
    try {
      const result = await api<Page<StudentListItem>>(`/staff/students?q=${encodeURIComponent(candidateQuery.trim())}`);
      setCandidateLookup({ submissionId, items: result.items });
      setError('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Pencarian gagal.');
    }
  };
  const linkCandidate = async (submissionId: string, studentId: string) => {
    setBusy(true);
    setError('');
    try {
      await api(`/staff/submissions/${submissionId}/link`, {
        method: 'POST',
        body: JSON.stringify({ studentId, reviewedConflict: true }),
      });
      setCandidateLookup(null);
      setCandidateQuery('');
      await loadData();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Data tidak dapat ditautkan.');
    } finally { setBusy(false); }
  };
  const validateImport = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) return setError('Pilih file spreadsheet terlebih dahulu.');
    const body = new FormData();
    body.append('file', file);
    body.append('academicYearId', academicYearId);
    body.append('level', level);
    setBusy(true);
    setError('');
    try {
      const result = await api<ImportResult>('/imports/validate', { method: 'POST', body });
      setBatch(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'File tidak dapat diperiksa.');
    } finally { setBusy(false); }
  };
  const commitImport = async () => {
    if (!batch) return;
    setBusy(true);
    setError('');
    try {
      await api(`/imports/${batch.batch.id}/commit`, { method: 'POST' });
      setBatch(null);
      setFile(null);
      setMessageToast('Impor selesai. Data yang tidak cocok tetap perlu ditinjau.');
      await loadData();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Impor tidak berhasil.');
    } finally { setBusy(false); }
  };
  const findImportCandidates = async (sourceRowNo: number) => {
    if (!batch) return;
    try {
      const result = await api<{ items: StudentListItem[] }>(`/imports/${batch.batch.id}/rows/${sourceRowNo}/candidates`);
      setImportCandidates({ sourceRowNo, items: result.items });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Calon pencocokan tidak dapat dimuat.');
    }
  };
  const resolveImportCandidate = async (sourceRowNo: number, studentId: string) => {
    if (!batch) return;
    setBusy(true);
    setError('');
    try {
      await api(`/imports/${batch.batch.id}/rows/${sourceRowNo}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ studentId }),
      });
      setBatch((current) => current ? {
        ...current,
        batch: { ...current.batch, quarantinedCount: Math.max(0, current.batch.quarantinedCount - 1) },
        preview: current.preview.map((row) => row.sourceRowNo === sourceRowNo
          ? { ...row, outcome: 'UPDATED', reasonCodes: row.reasonCodes.filter((code) => code !== 'NIPD_MATCH_REQUIRES_CONFIRMATION') }
          : row),
      } : null);
      setImportCandidates(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Pencocokan tidak berhasil.');
    } finally { setBusy(false); }
  };

  if (!user) return <main className="login-shell"><div className="login-card"><span className="brand-mark"><LockKeyhole size={22} /></span><p className="eyebrow">AREA PETUGAS</p><h1>Masuk ke data peserta didik</h1><p>Gunakan akun Google yang telah diberikan akses oleh administrator.</p>{error && <div className="alert">{error}</div>}<button className="button primary full" onClick={() => { window.location.href = '/api/auth/google'; }}>Lanjutkan dengan Google <ArrowRight size={17} /></button><a href="/" className="back-link">Kembali ke formulir pendaftaran</a></div></main>;

  return (
    <main className="staff-shell">
      <aside className="sidebar">
        <a className="brand side-brand" href="/staff"><span className="brand-mark"><BookOpenCheck size={20} /></span><span>DATA PESERTA DIDIK<small>ADMINISTRASI SEKOLAH</small></span></a>
        <div className="side-label">RUANG KERJA</div>
        <button className={`nav-item ${tab === 'students' ? 'active' : ''}`} onClick={() => setTab('students')}><UsersRound size={18} /> Peserta didik</button>
        <button className={`nav-item ${tab === 'teachers' ? 'active' : ''}`} onClick={() => setTab('teachers')}><UserRound size={18} /> Guru</button>
        <button className={`nav-item ${tab === 'submissions' ? 'active' : ''}`} onClick={() => setTab('submissions')}><UserRound size={18} /> Pendaftaran masuk</button>
        <button className={`nav-item ${tab === 'import' ? 'active' : ''}`} onClick={() => { setTab('import'); setBatch(null); }}><FileSpreadsheet size={18} /> Impor Dapodik</button>
        <div className="sidebar-bottom"><span className="staff-avatar">{user.fullName.charAt(0).toUpperCase()}</span><span className="staff-info"><strong>{user.fullName}</strong><small>{user.email}</small></span><button aria-label="Keluar" className="logout" onClick={() => void signOut()}>↗</button></div>
      </aside>
      <section className="staff-main">
        <header className="staff-topbar"><div className="breadcrumb">Sekolah <span>/</span> {tab === 'students' ? 'Peserta didik' : tab === 'teachers' ? 'Guru' : tab === 'submissions' ? 'Pendaftaran masuk' : 'Impor Dapodik'}</div><span className="access-label"><LockKeyhole size={14} /> Data terbatas</span></header>
        <div className="staff-content">
          {error && <div className="alert" role="alert">{error}</div>}
          {tab === 'students' && <><div className="page-title"><div><p className="eyebrow">DATABASE SEKOLAH</p><h1>Peserta didik</h1><p>Data resmi yang telah masuk dari berkas Dapodik.</p></div><button className="button primary" onClick={() => setTab('import')}><FileSpreadsheet size={16} /> Impor data</button></div><div className="toolbar"><div className="filter-bar"><div className="searchbox"><Search size={17} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama, NISN, atau NIPD…" /></div><select className="filter-select" aria-label="Tahun ajaran" value={studentFilters.academicYearId} onChange={(e) => setStudentFilters({ ...studentFilters, academicYearId: e.target.value })}><option value="">Semua tahun ajaran</option>{years.map((year) => <option key={year.id} value={year.id}>{year.label}</option>)}</select><select className="filter-select" aria-label="Jenjang" value={studentFilters.level} onChange={(e) => setStudentFilters({ ...studentFilters, level: e.target.value })}><option value="">Semua jenjang</option>{['KB', 'SD', 'SMP', 'SMA'].map((value) => <option key={value} value={value}>{value}</option>)}</select><select className="filter-select" aria-label="Kelas" value={studentFilters.classKey} onChange={(e) => setStudentFilters({ ...studentFilters, classKey: e.target.value })} disabled={classOptions.length === 0}><option value="">{classOptions.length ? 'Semua kelas' : 'Kelas belum tersedia'}</option>{classOptions.map((option) => <option key={`${option.level}-${option.classKey}`} value={option.classKey}>{studentFilters.level ? option.className ?? option.classKey : `${option.level} · ${option.className ?? option.classKey}`}</option>)}</select>{(studentFilters.level || studentFilters.classKey || studentFilters.academicYearId) && <button className="button secondary small" onClick={() => setStudentFilters(emptyStudentFilters)}>Bersihkan filter</button>}<span className="result-count">{studentTotal > students.length ? `${students.length} dari ${studentTotal} data` : `${students.length} data ditampilkan`}</span></div></div><div className="table-card"><table><thead><tr><th>Nama peserta didik</th><th>NISN</th><th>Jenjang / kelas</th><th>Tahun ajaran</th></tr></thead><tbody>{students.map((student) => { const enrollment = student.enrollments[0]; return <tr key={student.id}><td><strong>{student.fullName}</strong><small>NIPD {student.nipd || '—'}</small></td><td>{student.nisn || '—'}</td><td>{enrollment ? `${enrollment.level}${enrollment.grade ? ` · ${enrollment.grade}` : ''}${enrollment.className ? ` · ${enrollment.className}` : ''}` : 'Belum terdaftar'}</td><td>{enrollment?.academicYear.label || '—'}</td></tr>; })}{students.length === 0 && <tr><td colSpan={4} className="empty-state">Belum ada data untuk ditampilkan.</td></tr>}</tbody></table></div>{students.length < studentTotal && <div className="table-footer"><button className="button secondary small" onClick={() => void loadMoreStudents()}>Muat {studentTotal - students.length} data berikutnya</button></div>}</>}
          {tab === 'teachers' && <TeachersPanel years={years} />}
          {tab === 'submissions' && <><div className="page-title"><div><p className="eyebrow">VERIFIKASI SEKOLAH</p><h1>Pendaftaran masuk</h1><p>Data keluarga menunggu pencocokan dengan identitas Dapodik.</p></div></div><div className="status-tabs">{['PENDING', 'MATCHED', 'CONFLICT', 'CONFIRMED', 'REJECTED'].map((value) => <button key={value} className={status === value ? 'selected' : ''} onClick={() => setStatus(value)}>{value === 'PENDING' ? 'Menunggu' : value === 'MATCHED' ? 'Cocok' : value === 'CONFLICT' ? 'Konflik' : value === 'CONFIRMED' ? 'Dikonfirmasi' : 'Ditolak'}</button>)}</div><div className="submission-list">{submissions.map((submission) => <article className="submission-card" key={submission.id}><div className="submission-heading"><span className={`status-dot ${submission.status.toLowerCase()}`} /><div><h3>{submission.profile.fullName || 'Nama belum diisi'}</h3><p>Dikirim {new Date(submission.submittedAt).toLocaleDateString('id-ID')} · {submission.profile.enrollment?.level || 'Jenjang belum dipilih'} {submission.profile.enrollment?.academicYear || ''}</p></div><span className="status-badge">{submission.status === 'MATCHED' ? 'Cocok' : submission.status === 'CONFLICT' ? 'Konflik' : submission.status === 'PENDING' ? 'Menunggu' : submission.status}</span></div><div className="submission-identifiers"><span>NISN <strong>{submission.profile.nisn || '—'}</strong></span><span>NIK <strong>{submission.profile.nik || '—'}</strong></span>{submission.matchedStudent && <span>Cocok dengan <strong>{submission.matchedStudent.fullName}</strong></span>}</div>{submission.conflictCode && <div className="conflict-note">Identitas peserta didik mengarah ke data yang berbeda. Perlu pemeriksaan manual.</div>}{submission.status === 'CONFLICT' && <details className="manual-link"><summary>Telusuri data resmi dan tentukan tautan secara manual</summary><div className="manual-search"><input value={candidateQuery} onChange={(event) => setCandidateQuery(event.target.value)} placeholder="Nama, NISN, atau NIK resmi" /><button className="button secondary small" onClick={() => void findCandidates(submission.id)}>Cari</button></div>{candidateLookup?.submissionId === submission.id && <div className="candidate-list">{candidateLookup.items.map((candidate) => <button key={candidate.id} onClick={() => void linkCandidate(submission.id, candidate.id)} disabled={busy}><strong>{candidate.fullName}</strong><span>NISN {candidate.nisn || '—'} · NIK {candidate.nik || '—'}</span></button>)}{candidateLookup.items.length === 0 && <small>Tidak ada data resmi yang ditemukan.</small>}</div>}<small className="manual-hint">Tautan hanya diterima jika NISN atau NIK sama persis; keputusan dicatat dalam audit.</small></details>}<details className="profile-details"><summary>Lihat data formulir</summary><pre>{JSON.stringify(submission.profile, null, 2)}</pre></details><div className="submission-actions">{submission.status === 'MATCHED' && <button className="button primary small" disabled={busy} onClick={() => void review(submission.id, 'confirm')}><Check size={15} /> Konfirmasi kecocokan</button>}{!['CONFIRMED', 'REJECTED'].includes(submission.status) && <button className="button danger small" disabled={busy} onClick={() => void review(submission.id, 'reject')}>Tolak data</button>}</div></article>)}{submissions.length === 0 && <div className="empty-card">Tidak ada pendaftaran pada kategori ini.</div>}</div></>}
          {tab === 'import' && <><div className="page-title"><div><p className="eyebrow">SINKRONISASI DATA RESMI</p><h1>Impor Dapodik</h1><p>Unggah roster untuk ditinjau sebelum data apa pun diperbarui.</p></div></div><div className="import-layout"><form className="import-card" onSubmit={validateImport}><div className="import-icon"><FileSpreadsheet size={24} /></div><h2>Unggah berkas roster</h2><p>Format Dapodik 66 kolom atau lembar SMA 28 kolom. Berkas maksimal 8 MB.</p><label className="field"><span>Tahun ajaran</span><select value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)} required><option value="">Pilih tahun ajaran</option>{years.map((year) => <option key={year.id} value={year.id}>{year.label}</option>)}</select></label><label className="field"><span>Jenjang roster</span><select value={level} onChange={(e) => setLevel(e.target.value)}>{['KB', 'SD', 'SMP', 'SMA'].map((value) => <option key={value}>{value}</option>)}</select></label><label className="file-drop"><FileSpreadsheet size={22} /><strong>{file?.name || 'Pilih berkas spreadsheet'}</strong><small>Excel .xlsx atau SpreadsheetML .xls</small><input type="file" accept=".xlsx,.xls,.xlsm" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label><button className="button primary full" disabled={busy || !file}>{busy ? 'Memeriksa berkas…' : 'Periksa sebelum impor'} <ArrowRight size={17} /></button><div className="import-privacy"><ShieldCheck size={16} /><span>Data pribadi tidak ditampilkan dalam laporan baris. Berkas disimpan di penyimpanan privat.</span></div></form><aside className="import-guidance"><h3>Alur aman</h3><ol><li><b>Periksa</b><span>Sistem mengenali format, validasi identitas, dan menampilkan nomor baris serta kode masalah.</span></li><li><b>Tinjau</b><span>Baris bermasalah dikarantina; NIPD yang meragukan tidak digabung otomatis.</span></li><li><b>Impor</b><span>Data kosong tidak menghapus isian lama. Siswa yang tak muncul tidak otomatis dikeluarkan.</span></li></ol><div className="guidance-note"><LockKeyhole size={16} />Hanya petugas yang diizinkan dapat mengimpor data.</div></aside></div>{batch && <div className="preview-card">                    <div className="preview-title"><div><p className="eyebrow">PRATINJAU VALIDASI</p><h2>{batch.batch.rowCount} baris ditemukan</h2><p>{batch.batch.adapter} · {batch.batch.quarantinedCount} baris dikarantina</p></div><span className="status-badge">{batch.batch.status}</span></div>{batch.warningCodes.map((code) => <div className="warning-line" key={code}>{code === 'ACADEMIC_YEAR_CONFLICT' ? 'Tahun ajaran pada file berbeda dari pilihan. Pilihan pada formulir akan digunakan.' : 'Kolom yang diketahui salah label akan ditangani dengan aturan khusus.'}</div>)}<div className="preview-table-wrap"><table className="preview-table"><thead><tr><th>Baris</th><th>Hasil</th><th>Kode pemeriksaan</th><th>Tindakan</th></tr></thead><tbody>{batch.preview.map((row) => <tr key={row.sourceRowNo}><td>{row.sourceRowNo}</td><td>{row.outcome}</td><td>{row.reasonCodes.join(', ') || '—'}</td><td>{row.reasonCodes.includes('NIPD_MATCH_REQUIRES_CONFIRMATION') && <button className="button secondary small" onClick={() => void findImportCandidates(row.sourceRowNo)}>Pilih NIPD</button>}</td></tr>)}</tbody></table></div>{importCandidates && <div className="candidate-list import-candidate-list"><strong>Calon siswa dengan NIPD sama pada tahun dan jenjang ini</strong>{importCandidates.items.map((candidate) => <button key={candidate.id} onClick={() => void resolveImportCandidate(importCandidates.sourceRowNo, candidate.id)} disabled={busy}><strong>{candidate.fullName}</strong><span>NISN {candidate.nisn || '—'} · NIK {candidate.nik || '—'}</span></button>)}{importCandidates.items.length === 0 && <small>Tidak ditemukan calon yang dapat dikonfirmasi.</small>}</div>}<div className="preview-actions"><button className="button secondary" onClick={() => setBatch(null)}>Batalkan</button><button className="button primary" disabled={busy} onClick={() => void commitImport()}>Konfirmasi impor <Check size={16} /></button></div></div>}</>}
        </div>
      </section>
    </main>
  );
}

export default function App() {
  const [toast, setToast] = useState('');
  useEffect(() => registerToast(setToast), []);
  return <>
    {window.location.pathname === '/staff' ? <StaffApp /> : <PublicForm />}
    {toast && <div className="toast"><Check size={16} />{toast}<button onClick={() => setToast('')}>×</button></div>}
  </>;
}
